import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Loader2, Palette, Plus, Trash2, X } from "lucide-react";
import { customApi, type CustomListingDetail } from "../../lib/customListing";
import { authHeaders, getBackendUrl } from "../../lib/http";

// Murekkep kartelalari: magaza (alan) ve kategoriye (urun tipi) atanan sabit renk listeleri.
// Listede alicinin renk sectigi katmanlar atanan kartelayi kullanir; liste kendi secimini
// yapabilir (Otomatik = atama, Kartelasiz = modelin okudugu renkler).

interface InkPalette {
  id: string; name: string; colors: Array<{ label: string; hex: string }>;
  spaceIds: string[]; productTypeKeys: string[]; isDefault?: boolean;
  style?: { title?: string; accent?: string; background?: "collage" | "plain" };
}
const PRODUCT_TYPES: Array<[string, string]> = [
  ["tshirt", "Tişört"], ["sweatshirt", "Sweatshirt"], ["hoodie", "Kapüşonlu"], ["boxy_tee", "Boxy tee"], ["long_sleeve", "Uzun kollu"],
  ["tank", "Atlet"], ["baby", "Bebek"], ["ornament", "Ornament"], ["mug", "Kupa"], ["tumbler", "Termos"], ["other", "Diğer"],
];
const VIA_LABEL = { override: "bu listede seçildi", assigned: "atamadan", default: "varsayılan" } as const;
const api = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const r = await fetch(`${getBackendUrl()}${path}`, { method, headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
};

/** Listenin murekkep kartelasi: secim + bu listeden yeni kartela + yonetici */
export function InkPaletteCard({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const info = rec.inkPalette;
  const [manage, setManage] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const choose = useMutation({
    mutationFn: (paletteId: string) => customApi("POST", `/${rec.id}/ink-palette`, { paletteId }),
    onSuccess: () => { setErr(null); onChanged(); }, onError: (e) => setErr((e as Error).message),
  });
  const fromStudy = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/ink-palette/from-study`, {}),
    onSuccess: () => { setErr(null); onChanged(); }, onError: (e) => setErr((e as Error).message),
  });
  if (!info) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-3 text-sm">
      <Palette className="h-4 w-4 text-indigo-600" />
      <span className="font-medium text-gray-900">Mürekkep kartelası:</span>
      <span className="text-gray-700">{info.active ? `${info.active.name} (${info.active.count} renk, ${VIA_LABEL[info.active.via]})` : "Kartelasız: modelin okuduğu renkler"}</span>
      <label htmlFor="ink-choice" className="sr-only">Kartela seçimi</label>
      <select id="ink-choice" value={info.choice} onChange={(e) => choose.mutate(e.target.value)} disabled={choose.isPending}
        className="rounded-lg border border-gray-200 px-2 py-1 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
        <option value="auto">Otomatik (mağaza / kategori ataması)</option>
        <option value="none">Kartelasız (modelin renkleri)</option>
        {info.palettes.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.count}</option>)}
      </select>
      <button onClick={() => fromStudy.mutate()} disabled={fromStudy.isPending}
        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
        {fromStudy.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Bu listenin renklerinden yeni kartela
      </button>
      <button onClick={() => setManage(true)} className="text-xs font-medium text-indigo-600 hover:underline">Kartelaları yönet</button>
      {(choose.isPending) && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
      {err && <span className="w-full text-xs text-red-600">{err}</span>}
      {manage && <InkPaletteManager onClose={() => { setManage(false); onChanged(); }} />}
    </div>
  );
}

/** Tum kartelalar: renkler, atama (magaza + kategori), varsayilan, kartela gorunumu */
export function InkPaletteManager({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ["ink-palettes"], queryFn: () => api<InkPalette[]>("GET", "/api/ink-palettes") });
  const { data: spaces = [] } = useQuery({ queryKey: ["spaces"], queryFn: () => api<Array<{ id: string; name: string }>>("GET", "/api/spaces") });
  const [list, setList] = useState<InkPalette[]>([]);
  const [sel, setSel] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (data) setList(data); }, [data]);
  const save = useMutation({
    mutationFn: () => api<InkPalette[]>("PUT", "/api/ink-palettes", { palettes: list }),
    onSuccess: (r) => { setList(r); setErr(null); setSaved(true); setTimeout(() => setSaved(false), 2000); },
    onError: (e) => setErr((e as Error).message),
  });
  const p = list[sel];
  const upd = (patch: Partial<InkPalette>) => setList((l) => l.map((x, i) => (i === sel ? { ...x, ...patch } : x)));
  const toggle = (arr: string[], v: string) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const add = () => { setList((l) => [...l, { id: "", name: "Yeni kartela", colors: [{ label: "1 White", hex: "#ffffff" }, { label: "2 Black", hex: "#111111" }], spaceIds: [], productTypeKeys: [] }]); setSel(list.length); };
  const inputCls = "rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Mürekkep kartelaları">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Mürekkep kartelaları</h3>
          <button onClick={onClose} aria-label="Kapat" className="rounded p-1 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-[220px_1fr] overflow-hidden">
          <div className="space-y-1 overflow-y-auto border-r p-2">
            {isLoading && <Loader2 className="m-2 h-4 w-4 animate-spin text-gray-400" />}
            {list.map((x, i) => (
              <button key={x.id || i} onClick={() => setSel(i)} className={clsx("block w-full rounded-lg px-2 py-1.5 text-left text-sm", i === sel ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50")}>
                <span className="block truncate font-medium">{x.name}{x.isDefault ? " ★" : ""}</span>
                <span className="flex gap-0.5 pt-1">{x.colors.slice(0, 12).map((c) => <span key={c.label} className="h-2.5 w-2.5 rounded-full border border-gray-200" style={{ background: c.hex }} />)}</span>
              </button>
            ))}
            <button onClick={add} className="mt-1 inline-flex w-full items-center gap-1 rounded-lg border border-dashed border-gray-300 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50"><Plus className="h-3.5 w-3.5" /> Yeni kartela</button>
          </div>
          <div className="space-y-4 overflow-y-auto p-4">
            {!p ? <p className="text-sm text-gray-500">Kartela yok. "Yeni kartela" ile başla ya da bir listede "Bu listenin renklerinden yeni kartela" de.</p> : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="ink-name" className="sr-only">Kartela adı</label>
                  <input id="ink-name" value={p.name} onChange={(e) => upd({ name: e.target.value })} maxLength={60} className={`${inputCls} min-w-0 flex-1`} />
                  <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                    <input type="checkbox" checked={!!p.isDefault} onChange={(e) => setList((l) => l.map((x, i) => ({ ...x, isDefault: i === sel ? e.target.checked : e.target.checked ? false : x.isDefault })))} className="rounded border-gray-300 text-indigo-600" />
                    Varsayılan (atama yoksa)
                  </label>
                  <button onClick={() => { if (confirm(`"${p.name}" silinsin mi?`)) { setList((l) => l.filter((_, i) => i !== sel)); setSel(0); } }} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Sil</button>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium text-gray-700">Mağazalar (boş = hepsi)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {spaces.map((sp) => (
                      <button key={sp.id} onClick={() => upd({ spaceIds: toggle(p.spaceIds, sp.id) })} aria-pressed={p.spaceIds.includes(sp.id)}
                        className={clsx("rounded-full border px-2.5 py-0.5 text-xs", p.spaceIds.includes(sp.id) ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50")}>{sp.name}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-700">Kategoriler / ürün tipleri (boş = hepsi)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {PRODUCT_TYPES.map(([k, label]) => (
                      <button key={k} onClick={() => upd({ productTypeKeys: toggle(p.productTypeKeys, k) })} aria-pressed={p.productTypeKeys.includes(k)}
                        className={clsx("rounded-full border px-2.5 py-0.5 text-xs", p.productTypeKeys.includes(k) ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50")}>{label}</button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Öncelik: listede seçilen &gt; mağaza + kategori &gt; yalnız mağaza &gt; yalnız kategori &gt; varsayılan.</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium text-gray-700">Renkler ({p.colors.length}/30) · etiket en fazla 20 karakter (Etsy açılır listesi)</p>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {p.colors.map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span className="w-5 text-right text-xs text-gray-400">{i + 1}</span>
                        <input aria-label={`${i + 1}. renk adı`} value={c.label} maxLength={20} onChange={(e) => upd({ colors: p.colors.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)) })} className={`${inputCls} min-w-0 flex-1`} />
                        <input type="color" aria-label={`${c.label} rengi`} value={c.hex} onChange={(e) => upd({ colors: p.colors.map((x, k) => (k === i ? { ...x, hex: e.target.value } : x)) })} className="h-8 w-10 cursor-pointer rounded border border-gray-200" />
                        <button onClick={() => upd({ colors: p.colors.filter((_, k) => k !== i) })} aria-label={`${c.label} sil`} className="rounded p-1 text-gray-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  {p.colors.length < 30 && (
                    <button onClick={() => upd({ colors: [...p.colors, { label: `${p.colors.length + 1} `, hex: "#cccccc" }] })} className="mt-2 inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"><Plus className="h-3.5 w-3.5" /> Renk ekle</button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">Kartela görseli:</span>
                  <label htmlFor="ink-title" className="sr-only">Başlık</label>
                  <input id="ink-title" value={p.style?.title ?? ""} placeholder="Başlık (İngilizce), ör. Ink colors" onChange={(e) => upd({ style: { ...p.style, title: e.target.value } })} maxLength={60} className={`${inputCls} w-56`} />
                  <input type="color" aria-label="Vurgu rengi" value={p.style?.accent ?? "#b3263a"} onChange={(e) => upd({ style: { ...p.style, accent: e.target.value } })} className="h-8 w-10 cursor-pointer rounded border border-gray-200" />
                  <label htmlFor="ink-bg" className="sr-only">Zemin</label>
                  <select id="ink-bg" value={p.style?.background ?? "collage"} onChange={(e) => upd({ style: { ...p.style, background: e.target.value as "collage" | "plain" } })} className={inputCls}>
                    <option value="collage">Kolaj zeminli</option>
                    <option value="plain">Düz zemin</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
          <span className="text-xs text-gray-500">Kaydedince yeni liste çalışmalarına uygulanır; mevcut listede kartela seçimini yeniden yap (ya da "Otomatik"i seç) ki güncellensin.</span>
          <div className="flex items-center gap-2">
            {err && <span className="text-xs text-red-600">{err}</span>}
            {saved && <span className="text-xs text-emerald-700">Kaydedildi</span>}
            <button onClick={() => save.mutate()} disabled={save.isPending} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
